// TMOS Education Page
// Date: 2025-11-16
// Purpose: Display available education modules for users to learn about TMOS

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../../state/language/LanguageProvider';
import { useAuth } from '../../state/auth/AuthProvider';
import educationService, { EducationModule, EducationProgressSummary } from '../../services/educationService';
import {
  BookOpenIcon,
  CheckCircleIcon,
  PlayCircleIcon,
  AcademicCapIcon,
  ChartBarIcon
} from '@heroicons/react/24/outline';
import { CheckCircleIcon as CheckCircleIconSolid } from '@heroicons/react/24/solid';

export const EducationPage: React.FC = () => {
  const { t } = useLanguage();
  const { user } = useAuth();
  const navigate = useNavigate();

  const [modules, setModules] = useState<EducationModule[]>([]);
  const [summary, setSummary] = useState<EducationProgressSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (user?.id) {
      loadModules();
      loadSummary();
    }
  }, [user]);

  const loadModules = async () => {
    try {
      setLoading(true);
      setError(null);
      
      if (!user?.id) {
        setError('User not authenticated');
        return;
      }

      // Get user's role from user object or default
      const userRole = user.role || 'I_USER';
      const modulesData = await educationService.getEducationModules(user.id, userRole);
      setModules(modulesData);
    } catch (err: any) {
      console.error('Error loading education modules:', err);
      setError(err.response?.data?.message || 'Failed to load education modules');
    } finally {
      setLoading(false);
    }
  };

  const loadSummary = async () => {
    try {
      if (!user?.id) return;
      
      const summaryData = await educationService.getUserProgressSummary(user.id);
      setSummary(summaryData);
    } catch (err) {
      console.error('Error loading progress summary:', err);
    }
  };

  const handleModuleClick = (module: EducationModule) => {
    if (!module.id) {
      console.error('Module ID is missing:', module);
      return;
    }
    console.log('Navigating to module:', module.id, module.title);
    navigate(`/education/${module.id}`);
  };

  const getProgressColor = (progress: number = 0) => {
    if (progress === 100) return 'bg-[var(--color-success)]';
    if (progress >= 50) return 'bg-[var(--color-info)]';
    if (progress > 0) return 'bg-[var(--color-warning)]';
    return 'bg-[var(--color-border)]';
  };

  const formatProgress = (progress: number = 0) => {
    return Math.round(progress);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[var(--color-secondary-bg)] flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[var(--color-info)] mx-auto"></div>
          <p className="mt-4 text-[var(--color-secondary-text)]">Loading education modules...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[var(--color-secondary-bg)] flex items-center justify-center">
        <div className="text-center">
          <p className="text-[var(--color-error)] mb-4">{error}</p>
          <button
            onClick={loadModules}
            className="px-4 py-2 bg-[var(--color-primary-bg)] text-[var(--color-primary-text)] rounded-md hover:opacity-90"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[var(--color-secondary-bg)]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center space-x-3 mb-4">
            <AcademicCapIcon className="h-10 w-10 text-[var(--color-info)]" />
            <h1 className="text-3xl font-bold text-[var(--color-surface-text)]">
              {t('education.title') || 'Education Center'}
            </h1>
          </div>
          <p className="text-[var(--color-secondary-text)] text-lg">
            {t('education.subtitle') || 'Learn about T-MOS and how to use its features effectively'}
          </p>
        </div>

        {/* Progress Summary */}
        {summary && (
          <div className="bg-[var(--color-surface-bg)] rounded-lg shadow-sm border border-[var(--color-border)] p-6 mb-8">
            <div className="flex items-center space-x-2 mb-4">
              <ChartBarIcon className="h-6 w-6 text-[var(--color-info)]" />
              <h2 className="text-xl font-semibold text-[var(--color-surface-text)]">
                {t('education.yourProgress') || 'Your Progress'}
              </h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="text-center">
                <div className="text-3xl font-bold text-[var(--color-surface-text)]">{summary.total_modules}</div>
                <div className="text-sm text-[var(--color-secondary-text)]">{t('education.totalModules') || 'Total Modules'}</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-[var(--color-info)]">{summary.started_modules}</div>
                <div className="text-sm text-[var(--color-secondary-text)]">{t('education.started') || 'Started'}</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-[var(--color-success)]">{summary.completed_modules}</div>
                <div className="text-sm text-[var(--color-secondary-text)]">{t('education.completed') || 'Completed'}</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-[var(--color-info)]">
                  {formatProgress(summary.average_progress)}%
                </div>
                <div className="text-sm text-[var(--color-secondary-text)]">{t('education.averageProgress') || 'Avg Progress'}</div>
              </div>
            </div>
          </div>
        )}

        {/* Modules Grid */}
        {modules.length === 0 ? (
          <div className="bg-[var(--color-surface-bg)] rounded-lg shadow-sm border border-[var(--color-border)] p-12 text-center">
            <BookOpenIcon className="h-16 w-16 text-[var(--color-secondary-text)] opacity-50 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-[var(--color-surface-text)] mb-2">
              {t('education.noModules') || 'No Education Modules Available'}
            </h3>
            <p className="text-[var(--color-secondary-text)]">
              {t('education.noModulesDescription') || 'There are no education modules available for your role at this time.'}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {modules.map((module) => (
              <div
                key={module.id}
                onClick={() => handleModuleClick(module)}
                className="bg-[var(--color-surface-bg)] rounded-lg shadow-sm border border-[var(--color-border)] hover:shadow-md transition-shadow cursor-pointer overflow-hidden"
              >
                <div className="p-6">
                  {/* Module Header */}
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <div className="flex items-center space-x-2 mb-2">
                        <BookOpenIcon className="h-6 w-6 text-[var(--color-info)] flex-shrink-0" />
                        <h3 className="text-lg font-semibold text-[var(--color-surface-text)] line-clamp-2">
                          {module.title}
                        </h3>
                      </div>
                      {module.completed_at && (
                        <div className="flex items-center space-x-1 text-[var(--color-success)] text-sm mb-2">
                          <CheckCircleIconSolid className="h-4 w-4" />
                          <span>{t('education.completed') || 'Completed'}</span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Description */}
                  <p className="text-[var(--color-secondary-text)] text-sm mb-4 line-clamp-3">
                    {module.description}
                  </p>

                  {/* Progress Bar */}
                  {module.progress_percentage !== undefined && module.progress_percentage > 0 && (
                    <div className="mb-4">
                      <div className="flex items-center justify-between text-xs text-[var(--color-secondary-text)] mb-1">
                        <span>{t('education.progress') || 'Progress'}</span>
                        <span>{formatProgress(module.progress_percentage)}%</span>
                      </div>
                      <div className="w-full bg-[var(--color-border)] rounded-full h-2">
                        <div
                          className={`h-2 rounded-full transition-all ${getProgressColor(module.progress_percentage)}`}
                          style={{ width: `${module.progress_percentage}%` }}
                        />
                      </div>
                    </div>
                  )}

                  {/* Action Button */}
                  <div className="flex items-center justify-between pt-4 border-t border-[var(--color-border)]">
                    <span className="text-sm text-[var(--color-secondary-text)]">
                      {module.started_at
                        ? t('education.continue') || 'Continue Learning'
                        : t('education.start') || 'Start Learning'}
                    </span>
                    {module.completed_at ? (
                      <CheckCircleIcon className="h-5 w-5 text-[var(--color-success)]" />
                    ) : (
                      <PlayCircleIcon className="h-5 w-5 text-[var(--color-info)]" />
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

